import { useEffect, useRef, useState } from "react";
import { PortfolioItem } from "@/pages/Portfolio";
import ReelVideo from "./ReelVideo";
import { ArrowDown } from "lucide-react";
import { PortfolioDataItem } from "@/types/types";
import {
  getPortfolioMediaPath,
  sharePortfolioPath,
} from "@/lib/portfolioLinks";

interface VideosFeedProps {
  items: PortfolioItem[];
  group: PortfolioDataItem;
  initialIndex?: number;
  onActiveIndexChange?: (mediaIndex: number) => void;
  onBack?: () => void;
}

const VideosFeed: React.FC<VideosFeedProps> = ({
  items,
  group,
  initialIndex = 0,
  onActiveIndexChange,
  onBack,
}) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [isMuted, setIsMuted] = useState(false);
  const feedRef = useRef<HTMLDivElement | null>(null);
  const videoRefs = useRef<(HTMLDivElement | null)[]>([]);
  const activeIndexRef = useRef(0);
  const isPositioningRef = useRef(false);

  useEffect(() => {
    const nextIndex = Math.min(Math.max(initialIndex, 0), items.length - 1);
    isPositioningRef.current = true;
    activeIndexRef.current = nextIndex;
    setActiveIndex(nextIndex);

    const frame = window.requestAnimationFrame(() => {
      const feed = feedRef.current;
      const target = videoRefs.current[nextIndex];
      if (feed && target) feed.scrollTo({ top: target.offsetTop });

      window.requestAnimationFrame(() => {
        isPositioningRef.current = false;
      });
    });

    return () => window.cancelAnimationFrame(frame);
  }, [initialIndex, items]);

  const scrollToNextVideo = () => {
    const feed = feedRef.current;
    if (!feed) return;

    feed.scrollBy({
      top: feed.clientHeight,
      behavior: "smooth",
    });
  };

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];

        if (!visible) return;

        const nextIndex = Number(
          (visible.target as HTMLDivElement).dataset.index,
        );

        if (
          Number.isNaN(nextIndex) ||
          isPositioningRef.current ||
          nextIndex === activeIndexRef.current
        ) return;

        activeIndexRef.current = nextIndex;
        setActiveIndex(nextIndex);
        onActiveIndexChange?.(nextIndex);
      },
      {
        root: feedRef.current,
        threshold: [0.55, 0.7, 0.85],
      },
    );

    videoRefs.current.forEach((item) => {
      if (item) observer.observe(item);
    });

    return () => observer.disconnect();
  }, [items, onActiveIndexChange]);

  return (
    <section
      ref={feedRef}
      className="mx-auto h-[calc(100svh-7rem)] max-w-5xl snap-y snap-mandatory overflow-y-auto rounded-lg border-none bg-[#0d1821] [scrollbar-width:none] md:h-[calc(100svh-9rem)]"
      aria-label="Videos feed"
    >
      <div className="space-y-5">
        {items.map((item, index) => (
          <article
            key={`${item.src}-${index}`}
            ref={(el: HTMLDivElement | null) => {
              videoRefs.current[index] = el;
            }}
            data-index={index}
            className="grid min-h-[calc(100svh-10rem)] snap-start place-items-center gap-0 lg:gap-8 lg:grid-cols-[minmax(280px,430px)_minmax(0,1fr)] lg:pb-4"
          >
            <ReelVideo
              item={item}
              isActive={index === activeIndex}
              isMuted={isMuted}
              onMutedChange={setIsMuted}
              onBack={onBack}
              onShare={() =>
                sharePortfolioPath(
                  `${item.brand} video ${index + 1}`,
                  getPortfolioMediaPath(group, index),
                )
              }
            />

            <div className="w-full max-w-xl pb-3 font-poppins lg:pb-0">
              <p className="text-sm font-semibold uppercase tracking-[0.2em] text-[#00a878]">
                {String(index + 1).padStart(2, "0")} /{" "}
                {String(items.length).padStart(2, "0")}
              </p>

              {item.brand && (
                <p className="hidden lg:block mt-4 font-orbitron text-xl font-medium lg:text-3xl">
                  {item.brand}
                </p>
              )}
              <h2 className="hidden lg:block mt-2 text-lg font-medium text-[#c3f620]">
                {item.title}
              </h2>
              <p className="hidden lg:block mt-2 lg:mt-4 max-w-lg text-sm leading-4 lg:leading-7 text-[#f5f5f5]/72 md:text-base">
                {item.description}
              </p>
            </div>
          </article>
        ))}
      </div>
      {items.length > 1 && activeIndex < items.length - 1 && (
        <button
          type="button"
          onClick={scrollToNextVideo}
          className="sticky bottom-4 left-1/2 z-20 grid h-11 w-11 -translate-x-1/2 place-items-center rounded-full border border-[#f5f5f5]/15 bg-black/55 text-[#f5f5f5] shadow-xl backdrop-blur outline-none ring-[#00a878] transition hover:border-[#00a878] hover:text-[#c3f620] focus-visible:ring-2"
          aria-label="Scroll to view more videos"
        >
          <ArrowDown className="h-5 w-5" />
        </button>
      )}
    </section>
  );
};

export default VideosFeed;
